import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  DoCheck
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType, Chart, registerables } from 'chart.js';

Chart.register(...registerables);

@Component({
  selector: 'app-bar-chart',
  standalone: true,
  imports: [CommonModule, BaseChartDirective],
  templateUrl: './bar-chart.component.html',
  styleUrl: './bar-chart.component.scss'
})
export class BarChartComponent implements AfterViewInit, OnChanges, OnDestroy, DoCheck {
  @ViewChild(BaseChartDirective) chart!: BaseChartDirective;

  @Input() groupedBarData: any[] = [];
  @Input() xAxisLabel: string = 'Brand';
  @Input() yAxisLabel: string = 'Score';
  @Input() legendTitle: string = 'LLM Models';
  /** 'bar' = bar chart, 'line' = multi-line chart (same data, line style) */
  @Input() chartType: 'bar' | 'line' = 'bar';
  /** When true, show custom legend with domain favicon + name + color (for Source Usage by Domain chart) */
  @Input() showDomainLegend: boolean = false;

  /** Legend items for custom domain legend (name, color, faviconUrl) */
  legendItems: { name: string; color: string; faviconUrl: string }[] = [];

  barChartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: []
  };

  barChartType: ChartType = 'bar'; // set in updateChartData from chartType

  barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    scales: {
      x: {
        display: true,
        title: { display: true, text: '' }
      },
      y: {
        display: true,
        beginAtZero: true,
        title: { display: true, text: '' }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const label = ctx.label || '';
            const value = ctx.parsed?.y ?? ctx.parsed ?? '';
            return `${label}: ${value}`;
          }
        }
      }
    }
  };

  private lastDataSignature = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['groupedBarData'] || changes['xAxisLabel'] || changes['yAxisLabel'] || changes['chartType'] || changes['showDomainLegend']) {
      this.lastDataSignature = this.buildDataSignature();
      this.updateChartData();
    }
  }

  ngDoCheck(): void {
    const sig = this.buildDataSignature();
    if (sig !== this.lastDataSignature) {
      this.lastDataSignature = sig;
      this.updateChartData();
    }
  }

  ngAfterViewInit(): void {
    this.updateChartData();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize.bind(this));
    }
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize.bind(this));
    }
  }

  private onResize(): void {
    setTimeout(() => {
      if (this.chart?.chart) {
        this.chart.chart.resize();
        this.chart.update('none');
      }
    }, 100);
  }

  private buildDataSignature(): string {
    try {
      const data = Array.isArray(this.groupedBarData)
        ? this.groupedBarData.map((it: any) => [it?.name, it?.value, it?.series?.length])
        : [];
      return JSON.stringify({ data, x: this.xAxisLabel, y: this.yAxisLabel, chartType: this.chartType });
    } catch {
      return Math.random().toString();
    }
  }

  private updateChartData(): void {
    const data = this.groupedBarData;
    if (!Array.isArray(data) || data.length === 0) {
      this.barChartData = { labels: [], datasets: [] };
      if (this.chart?.chart) this.chart.update('none');
      return;
    }

    const first = data[0];
    const isGrouped = first && Array.isArray(first?.series);

    let labels: string[];
    let values: number[];
    let datasets: Array<{ label: string; data: number[]; backgroundColor?: string | string[]; borderColor?: string | string[]; borderWidth?: number }>;

    const isLine = this.chartType === 'line';

    if (isGrouped) {
      // Grouped: each group has name + series. One dataset per group (bars or lines).
      const firstSeries = first.series || [];
      labels = firstSeries.map((s: any) => s?.name ?? '');
      datasets = data.map((group: any, idx: number) => {
        const series = Array.isArray(group.series) ? group.series : [];
        const arr = labels.map((label) => {
          const point = series.find((s: any) => (s?.name ?? '') === label);
          return Number(point?.value) ?? 0;
        });
        const color = this.getBarColor(idx);
        const base: any = {
          label: group?.name ?? `Series ${idx + 1}`,
          data: arr,
          borderColor: color,
          borderWidth: isLine ? 2 : 1
        };
        if (isLine) {
          base.backgroundColor = color;
          base.fill = false;
          base.tension = 0.2;
          base.pointRadius = 4;
          base.pointHoverRadius = 6;
        } else {
          base.backgroundColor = color;
        }
        return base;
      });
    } else {
      // Flat: [{ name, value }, ...] — single series (one bar set or one line)
      labels = data.map((item: any) => item?.name ?? '');
      values = data.map((item: any) => Number(item?.value) ?? 0);
      const color = 'rgba(25, 118, 210, 0.8)';
      const lineColor = 'rgba(25, 118, 210, 1)';
      datasets = [{
        label: this.legendTitle || 'Value',
        data: values,
        backgroundColor: color,
        borderColor: lineColor,
        borderWidth: isLine ? 2 : 1,
        ...(isLine && {
          fill: false,
          tension: 0.2,
          pointRadius: 4,
          pointHoverRadius: 6
        })
      }];
    }

    this.barChartType = isLine ? 'line' : 'bar';
    this.barChartData = {
      labels,
      datasets
    };

    // Build custom domain legend items when showDomainLegend and grouped line chart
    if (this.showDomainLegend && isGrouped && isLine && data.length > 0) {
      this.legendItems = data.map((group: any, idx: number) => ({
        name: group?.name ?? `Series ${idx + 1}`,
        color: this.getBarColor(idx),
        faviconUrl: this.getFaviconUrl(group?.name ?? '')
      }));
    } else {
      this.legendItems = [];
    }

    const useCustomLegend = this.showDomainLegend && this.legendItems.length > 0;

    this.barChartOptions = {
      ...this.barChartOptions,
      scales: {
        x: {
          display: true,
          title: { display: !!this.xAxisLabel, text: this.xAxisLabel }
        },
        y: {
          display: true,
          beginAtZero: true,
          title: { display: !!this.yAxisLabel, text: this.yAxisLabel }
        }
      },
      plugins: {
        legend: {
          display: isLine && datasets.length > 1 && !useCustomLegend,
          position: 'top'
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const label = ctx.label || '';
              const value = ctx.parsed?.y ?? ctx.parsed ?? '';
              return `${label}: ${value}`;
            }
          }
        }
      }
    };

    if (this.chart?.chart) {
      this.chart.update('none');
    }
  }

  private getBarColor(index: number): string {
    const palette = [
      'rgba(25, 118, 210, 0.8)',
      'rgba(156, 39, 176, 0.8)',
      'rgba(0, 150, 136, 0.8)',
      'rgba(255, 152, 0, 0.8)',
      'rgba(76, 175, 80, 0.8)',
      'rgba(244, 67, 54, 0.8)'
    ];
    return palette[index % palette.length];
  }

  onFaviconError(event: Event): void {
    const el = event.target as HTMLImageElement | null;
    if (el?.style) el.style.display = 'none';
  }

  getFaviconUrl(domain: string): string {
    let d = (domain || '').trim();
    try {
      if (d && !d.startsWith('http')) d = 'https://' + d;
      const host = d ? new URL(d).hostname : '';
      return host ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32` : '';
    } catch {
      return '';
    }
  }
}
